import {
  getDocFromCache,
  getDocFromServer,
  getDocsFromCache,
  getDocsFromServer,
} from "firebase/firestore";

export const LIST_CACHE_MAX_AGE_MS = 2 * 60 * 1000;
export const DETAIL_CACHE_MAX_AGE_MS = 60 * 1000;
export const COUNT_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const FRESHNESS_PREFIX = "firestore-freshness:";
const VALUE_PREFIX = "firestore-value:";

const getSessionStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

const readSessionEntry = (key) => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawValue = storage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const writeSessionEntry = (key, value) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore quota/storage failures and continue without the extra cache metadata.
  }
};

const getFreshnessKey = (cacheKey) => `${FRESHNESS_PREFIX}${cacheKey}`;
const getValueKey = (cacheKey) => `${VALUE_PREFIX}${cacheKey}`;

const getFreshnessTimestamp = (cacheKey) => {
  const entry = readSessionEntry(getFreshnessKey(cacheKey));
  return typeof entry?.updatedAt === "number" ? entry.updatedAt : 0;
};

export const isSessionValueFresh = (cacheKey, maxAgeMs) => {
  if (!cacheKey) {
    return false;
  }

  const updatedAt = getFreshnessTimestamp(cacheKey);
  return updatedAt > 0 && Date.now() - updatedAt <= maxAgeMs;
};

export const markSessionValueFresh = (cacheKey) => {
  if (!cacheKey) {
    return;
  }

  writeSessionEntry(getFreshnessKey(cacheKey), { updatedAt: Date.now() });
};

export const getFreshSessionValue = (cacheKey, maxAgeMs) => {
  if (!cacheKey || !isSessionValueFresh(cacheKey, maxAgeMs)) {
    return null;
  }

  const entry = readSessionEntry(getValueKey(cacheKey));
  return entry?.value ?? null;
};

export const setSessionValue = (cacheKey, value) => {
  if (!cacheKey) {
    return;
  }

  writeSessionEntry(getValueKey(cacheKey), {
    updatedAt: Date.now(),
    value,
  });
  markSessionValueFresh(cacheKey);
};

const readFromCache = async (reader) => {
  try {
    return await reader();
  } catch (error) {
    return null;
  }
};

export const loadQueryWithCache = async ({
  queryRef,
  cacheKey,
  maxAgeMs = LIST_CACHE_MAX_AGE_MS,
  forceServer = false,
  onCache,
  onServer,
}) => {
  const cacheSnapshot = await readFromCache(() => getDocsFromCache(queryRef));
  const canServeCacheOnly =
    Boolean(cacheSnapshot) && !forceServer && isSessionValueFresh(cacheKey, maxAgeMs);
  const shouldApplyCacheSnapshot =
    Boolean(cacheSnapshot) && (canServeCacheOnly || !cacheSnapshot.empty);

  if (shouldApplyCacheSnapshot && onCache) {
    await onCache(cacheSnapshot);
  }

  if (canServeCacheOnly) {
    return { cacheSnapshot, serverSnapshot: null, usedServer: false };
  }

  try {
    const serverSnapshot = await getDocsFromServer(queryRef);
    markSessionValueFresh(cacheKey);

    if (onServer) {
      await onServer(serverSnapshot);
    }

    return { cacheSnapshot, serverSnapshot, usedServer: true };
  } catch (error) {
    if (cacheSnapshot) {
      return { cacheSnapshot, serverSnapshot: null, usedServer: false, error };
    }

    throw error;
  }
};

export const loadDocWithCache = async ({
  docRef,
  cacheKey,
  maxAgeMs = DETAIL_CACHE_MAX_AGE_MS,
  forceServer = false,
  onCache,
  onServer,
}) => {
  const cacheSnapshot = await readFromCache(() => getDocFromCache(docRef));
  const canServeCacheOnly =
    Boolean(cacheSnapshot) && !forceServer && isSessionValueFresh(cacheKey, maxAgeMs);
  const shouldApplyCacheSnapshot =
    Boolean(cacheSnapshot) && (canServeCacheOnly || cacheSnapshot.exists());

  if (shouldApplyCacheSnapshot && onCache) {
    await onCache(cacheSnapshot);
  }

  if (canServeCacheOnly) {
    return { cacheSnapshot, serverSnapshot: null, usedServer: false };
  }

  try {
    const serverSnapshot = await getDocFromServer(docRef);
    markSessionValueFresh(cacheKey);

    if (onServer) {
      await onServer(serverSnapshot);
    }

    return { cacheSnapshot, serverSnapshot, usedServer: true };
  } catch (error) {
    if (cacheSnapshot) {
      return { cacheSnapshot, serverSnapshot: null, usedServer: false, error };
    }

    throw error;
  }
};
